import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { deriveConfigurationWeldParams } from "../../helpers/common/ag-grid-helper";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";
import { tlogMapData } from "../../helpers/tlog/tlog-data-mapper";
import { tlogHelper } from "../../helpers/tlog/tlog-helper";
import { GlobalUnitEnum } from "../../helpers/unit-conversion";
import { startTlogsLoader } from "../../redux/tLogData/actions";
import { FETCH_TLOGS_SAGA } from "../../redux/tLogData/type";
import TlogGrid from "../AgGrid/Tlog-View";
import CommonLoader from "../Loaders/loader";

// Custom hook for Tlog data management
const useTlogData = ({
  s_deviceid_event,
  station_number,
  type,
  zone,
  weld_number,
  boundary,
  tilt_start,
  tilt_end,
  isM500,
  rawZoneView,
  view,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const reducer = useSelector((state) => state.tlogsReducer);
  const { projectReducer } = useSelector((state) => state);

  const { weld_no, Bugtype, deviceid } = location.state || {};
  const isFromPQRTab = weld_no && Bugtype && deviceid;

  const [weldParamsShowColumns, setWeldParamsShowColumns] = useState([]);
  const [reportColumns, setReportColumns] = useState(
    logDataHelperFunctions.sampleReportColumns
  );

  // Fetch and parse report columns
  useEffect(() => {
    if (projectReducer?.selectedProject?.reportColumns) {
      try {
        const parsedData = JSON.parse(
          projectReducer?.selectedProject?.reportColumns
        );
        setReportColumns(parsedData);
      } catch (e) {
        console.error("Error parsing report columns:", e);
      }
    }
  }, [projectReducer?.selectedProject?.reportColumns]);

  // Process weld parameters
  useEffect(() => {
    if (reducer?.tlogsList?.weldParams?.length > 0) {
      const allMappedValues = reducer?.tlogsList?.weldParams
        ?.map((ite) => {
          try {
            const params =
              typeof ite.parameter_check_list === "string"
                ? JSON.parse(ite.parameter_check_list) || {}
                : ite.parameter_check_list || {};

            const enabledKeys = Object.keys(params).filter(
              (key) => params[key]
            );

            return {
              job_number: ite.job_number,
              enabledKeys:
                enabledKeys.length > 0
                  ? enabledKeys
                  : logDataHelperFunctions.defaultParamsValues,
            };
          } catch (e) {
            console.error("Error processing weld params:", e);
            return null;
          }
        })
        .filter(Boolean);

      setWeldParamsShowColumns(allMappedValues);
    }
  }, [reducer?.tlogsList?.weldParams]);

  // Fetch Tlog data for non-PQR tab
  useEffect(() => {
    const selectedProject = projectReducer?.selectedProject;
    if (selectedProject && !isFromPQRTab) {
      const params = {
        project_id: selectedProject.project_id,
        project_name: selectedProject.project_name,
        deviceid_event: s_deviceid_event,
        weld_number: weld_number,
        zone: isM500 ? rawZoneView : zone,
        type: type,
      };

      dispatch(startTlogsLoader());
      dispatch({ type: FETCH_TLOGS_SAGA, payload: params });
    }
  }, [
    dispatch,
    projectReducer?.selectedProject,
    s_deviceid_event,
    weld_number,
    zone,
    type,
    isM500,
    rawZoneView,
  ]);

  // Fetch Tlog data for PQR tab
  useEffect(() => {
    const selectedProject = projectReducer?.selectedProject;
    if (selectedProject && isFromPQRTab) {
      const params = {
        project_id: selectedProject.project_id,
        project_name: selectedProject.project_name,
        deviceid_event: deviceid,
        weld_number: weld_no,
        type: Bugtype,
      };

      dispatch(startTlogsLoader());
      dispatch({ type: FETCH_TLOGS_SAGA, payload: params });
    }
  }, [
    dispatch,
    projectReducer?.selectedProject,
    isFromPQRTab,
    deviceid,
    weld_no,
    Bugtype,
  ]);

  // Memoized tlog data processing
  const tlogData = useMemo(
    () => reducer?.tlogsList?.allTlogs?.recordList || [],
    [reducer?.tlogsList?.allTlogs?.recordList]
  );

  const tableLimitData = useMemo(
    () => reducer?.tlogsList?.tableLimitData?.recordList || [],
    [reducer?.tlogsList?.tableLimitData?.recordList]
  );

  const loader = useMemo(
    () => reducer?.isTlogsLoading,
    [reducer?.isTlogsLoading]
  );

  // Memoized tlog parsed data
  const tlogParsedData = useMemo(() => {
    return (
      tlogMapData({
        isFromPQRTab,
        station_number,
        tableLimitData,
        tlogRecords: tlogData,
        type,
        reportColumns,
        weldParamsShowColumns,
        boundary,
        tilt_start,
        tilt_end,
        view,
        projectReducer,
      }) || []
    );
  }, [
    isFromPQRTab,
    station_number,
    tableLimitData,
    tlogData,
    type,
    reportColumns,
    weldParamsShowColumns,
    boundary,
    tilt_start,
    tilt_end,
    view,
    projectReducer,
  ]);

  return {
    tlogData,
    tableLimitData,
    loader,
    tlogParsedData,
    isFromPQRTab,
    reportColumns,
  };
};

// Custom hook for table configuration
const useTableConfiguration = ({
  view,
  tlogParsedData,
  reportColumns,
  selectedProject,
}) => {
  const hideColumnsStationZero = [
    "tilt",
    "distance",
    "wireSpeed",
    "oscillationWidth",
    "target",
    "horizontalBias",
    "frequency",
    "totalWireConsumed",
    "trueEnergy",
  ];

  const showRangeColumns = [
    "volts",
    "current",
    "wireSpeed",
    "oscillationWidth",
    "trueEnergy",
    "heat",
    "travelSpeed",
  ];

  const getRangeColumn = ({
    avgTlogItem,
    tableLimitData,
    type,
    column,
    calculationLevel,
    isFromPQRTab = false,
  }) => {
    if (!avgTlogItem?.trail_pass_name && !avgTlogItem?.lead_pass_name) {
      return {
        doesExist: false,
        showColor: "",
      };
    }

    let passNameExtracted = "";
    if (type === "Lead") {
      passNameExtracted = avgTlogItem?.lead_pass_name;
    } else if (type === "Trail") {
      passNameExtracted = avgTlogItem?.trail_pass_name;
    } else {
      passNameExtracted =
        avgTlogItem?.trail_pass_name || avgTlogItem?.lead_pass_name;
    }

    if (passNameExtracted.includes("Head")) {
      passNameExtracted = "HEAD";
    }

    const job_number = avgTlogItem?.job_number;

    let allTableLimitZones = tableLimitData?.filter(
      (tableLimit) => tableLimit.job_number === job_number
    );

    allTableLimitZones = allTableLimitZones?.filter(
      (tableLimit) => tableLimit.zone_name === passNameExtracted
    );

    const allPassNames = allTableLimitZones?.map((ite) => ite.pass_name);
    const uniquePassNames = [...new Set(allPassNames)];

    if (uniquePassNames?.length === 0) {
      return {
        doesExist: false,
        showColor: "",
      };
    }

    const zonePrefix = calculationLevel === "zone_level" ? "zone_" : "";
    const minColumn = `${zonePrefix}${column}_min`;
    const maxColumn = `${zonePrefix}${column}_max`;

    const hasMinMax =
      allTableLimitZones?.[0]?.[minColumn] &&
      allTableLimitZones?.[0]?.[maxColumn];

    return {
      doesExist: true,
      showColor: hasMinMax
        ? `[${tlogHelper.rangeCalculation(
            allTableLimitZones?.[0]?.[minColumn],
            column,
            reportColumns,
            isFromPQRTab
          )} - ${tlogHelper.rangeCalculation(
            allTableLimitZones?.[0]?.[maxColumn],
            column,
            reportColumns,
            isFromPQRTab
          )}]`
        : "",
    };
  };

  const uniqueJobNumbers = useMemo(
    () => [...new Set(tlogParsedData?.map((ite) => ite.jobNumber))],
    [tlogParsedData]
  );

  const { hideStatus, hideHeaderLimits, calculation_level } = useMemo(
    () =>
      deriveConfigurationWeldParams({
        weldParams: selectedProject?.weldParameters,
        view: `${view}_tlog`,
        jobNumbers: uniqueJobNumbers,
        statusCalculationMethod: selectedProject?.statusCalculationMethod,
      }) || {},
    [
      selectedProject?.weldParameters,
      view,
      uniqueJobNumbers,
      selectedProject?.statusCalculationMethod,
    ]
  );

  return {
    hideColumnsStationZero,
    showRangeColumns,
    getRangeColumn,
    hideStatus,
    hideHeaderLimits,
    calculation_level,
  };
};

const TlogTableView = ({
  s_deviceid_event,
  station_number,
  type,
  zone,
  weld_number,
  setSelectedRowKeys,
  selectedRowKeys = [],
  boundary,
  tilt_start,
  tilt_end,
  isM500,
  rawZoneView,
  view,
}) => {
  const { t } = useTranslation();

  console.log(type, "typetypetypetype");
  // Custom hooks for data and configuration management
  const {
    tlogData,
    tableLimitData,
    loader,
    tlogParsedData,
    isFromPQRTab,
    reportColumns,
  } = useTlogData({
    s_deviceid_event,
    station_number,
    type,
    zone,
    weld_number,
    boundary,
    tilt_start,
    tilt_end,
    isM500,
    rawZoneView,
    view,
  });

  const {
    hideColumnsStationZero,
    showRangeColumns,
    getRangeColumn,
    hideStatus,
    hideHeaderLimits,
    calculation_level,
  } = useTableConfiguration({
    view,
    tlogParsedData,
    reportColumns,
    selectedProject: useSelector(
      (state) => state.projectReducer?.selectedProject
    ),
  });

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const hasValidM500Data = useMemo(
    () =>
      tlogParsedData.some(
        (record) =>
          record.headAngle !== "" ||
          record.arcImpedance !== "" ||
          (record.m500Data && record.m500Data.PipeNum !== "-")
      ),
    [tlogParsedData]
  );

  const statusCalculationMethod = useSelector(
    (state) => state.projectReducer?.selectedProject?.statusCalculationMethod
  );

  const globalUnit = useSelector(
    (state) =>
      state.projectReducer?.selectedProject?.globalUnit ||
      GlobalUnitEnum.IMPERIAL
  );

  // Render logic
  const renderTlogContent = () => {
    if (loader) {
      return (
        <div className="slogdata-loader">
          <CommonLoader />
        </div>
      );
    }

    if (tlogParsedData.length === 0) {
      return (
        <div className="slog-NoData">
          <p>{t("sLogData.noDataAvailable")}</p>
        </div>
      );
    }

    const gridProps = {
      reportColumns,
      getRangeColumn,
      hasValidM500Data,
      hideColumnsStationZero,
      isFromPQRTab,
      isM500,
      showRangeColumns,
      station_number,
      tableLimitData,
      type,
      rows: tlogParsedData,
      tlogData,
      loading: loader,
      statusCalculationMethod,
      globalUnit,
    };

    return isFromPQRTab ? (
      <TlogGrid
        {...gridProps}
        showRowSelection={true}
        selectedRowKeys={selectedRowKeys}
        onRowSelected={onSelectChange}
        tableHeight={-80}
      />
    ) : (
      <TlogGrid
        {...gridProps}
        hideStatus={hideStatus}
        hideHeaderLimits={hideHeaderLimits}
        calculation_level={calculation_level}
        tableHeight={-240}
      />
    );
  };

  return <div>{renderTlogContent()}</div>;
};

export default TlogTableView;
